import React, { useEffect, useState } from 'react';
import { ParsedPromo } from '../../components/Layout/Promotions/types';
import { UserTags } from '../useFetchPlatformPromos/types';
import { checkIsContentOnlyPromo } from '../../components/Layout/Promotions/utils';
import { checkIsUrlExternal } from '../../utils/links-helper/links-helper';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useSignIn } from '../auth';
import { useSnackbar } from '../snackbar';
import { useTranslations } from '../useTranslationsHelper';
import { optInXPPromo } from '../../components/Layout/Promotions/services';
import { setLoadPlatformPromotions } from '../../state/promotions/reducers';
import { useDispatch } from 'react-redux';
import { storageClaimedPromo } from '../../utils/storage';
import { useUserContext } from '../../providers/UserProvider';
import { AxiosError } from 'axios';
import { getActualXPToken } from '../../utils/auth';

type HandleClaimClick = (promo?: ParsedPromo) => void;

type UsePromotionClaimProps = {
  setHandleClaimClick: React.Dispatch<React.SetStateAction<HandleClaimClick>>;
  userTags?: Nullable<UserTags>;
};

export const usePromoClaim = ({ setHandleClaimClick }: UsePromotionClaimProps): void => {
  const { t } = useTranslations();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { addSnack } = useSnackbar();
  const { XPToken, setXPToken } = useUserContext();

  const signIn = useSignIn();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [startClaim, setStartClaim] = useState<boolean>(false);
  const [claimedPromo, setClaimedPromo] = useState<ParsedPromo | null>(null);
  const [storedPromo, setStoredPromo] = useState<ParsedPromo | null>(null);
  const [shouldClaimPromo, setShouldClaimPromo] = useState<boolean>(false);

  const onPromotionClaimed = (): void => {
    addSnack({
      theme: 'dark',
      type: 'success',
      message: t('promotions.claimed'),
    });
  };

  const onClaimedError = (error: Record<string, string>): void => {
    addSnack({
      theme: 'dark',
      type: 'error',
      message: error.message || t('error.common'),
      isDismissible: false,
    });
  };

  const onActivateBonus = async (): Promise<void> => {
    const { platformId } = claimedPromo || {};

    try {
      const activeToken = await getActualXPToken(XPToken, setXPToken);

      await optInXPPromo(platformId, activeToken);

      onPromotionClaimed();
    } catch (error) {
      onClaimedError((error as AxiosError)?.response?.data as Record<string, string>);
    } finally {
      setStartClaim(false);
      dispatch(setLoadPlatformPromotions(true));
    }
  };

  const goToCtaLink = (): void => {
    const ctaUrl = String(claimedPromo?.ctaUrl);

    if (checkIsUrlExternal(ctaUrl)) {
      window.location.href = String(ctaUrl);
    } else {
      navigate(`/${ctaUrl}`);
    }
  };

  const claimHandler: HandleClaimClick = (promo?: ParsedPromo): void => {
    promo && setClaimedPromo(promo);
    setStartClaim(true);
  };

  useEffect(() => {
    setHandleClaimClick(() => claimHandler);
  }, []);

  useEffect(() => {
    if (shouldClaimPromo && XPToken) {
      onActivateBonus();
      setShouldClaimPromo(false);
    }
  }, [shouldClaimPromo, XPToken]);

  useEffect(() => {
    if (startClaim) {
      const isContentOnlyPromo = checkIsContentOnlyPromo(claimedPromo);
      const shouldGoToCtaLink = isContentOnlyPromo || claimedPromo?.isActive;

      if (isAuthenticated) {
        shouldGoToCtaLink ? goToCtaLink() : setShouldClaimPromo(true);
      } else {
        storageClaimedPromo.set({ value: JSON.stringify(claimedPromo) });
        signIn({ redirectUri: window.location.href });
      }
    }
  }, [startClaim]);

  useEffect(() => {
    if (storedPromo) {
      setClaimedPromo(storedPromo);
      setStartClaim(true);
    }
  }, [storedPromo]);

  // Extract previously opted promotion and set it to claim for logged in users and remove it from a session storage
  useEffect(() => {
    if (!isAuthLoading) {
      const claimedPromoStorage: ParsedPromo = JSON.parse(String(storageClaimedPromo.get()));

      claimedPromoStorage && isAuthenticated && setStoredPromo(claimedPromoStorage);
      storageClaimedPromo.remove();
    }
  }, [isAuthLoading]);
};
